import {
  addNodesInTree,
  getNodeInTree,
  removeNodesFromTree,
  updateMatchingNodesInTree,
} from '../../../components/Tree/helpers/helpers';
import type { NodeBase, NodeBaseItem } from '../../../entities/Node';
import { getCatalogId } from '../../../utils/tree/ids';
import { createUpdatedNode } from '../../../utils/tree/node';
import { getNodeType } from '../../../utils/tree/nodeItem';
import { destructId } from '../../../utils/tree/treeNode';
import { NODE_TYPE_CATEGORY, NODE_TYPE_ITEM } from '../../../utils/types/nodeTypes';
import {
  selectCurrentCommand,
  selectTreeDataByCatalogId,
  updateData,
} from '../../selectors/tree/tree';
import { catalogConfigSelector } from '../../selectors/tree/data';
import { pasteNode } from '../../slices/tree';
import { newCopyPasteError } from '../error/newCopyPasteError';
import { toastMessage } from '../../slices/toaster';
import { pasteMoveCategory } from './pasteMoveCategory';
import { pasteMoveItem } from './pasteMoveItem';
import type { AppDispatch, RootState } from '../../index';

export const pasteMove =
  (targetTree: string, targetNodeItem: NodeBaseItem) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const { nodeItems, tree: sourceTree } = selectCurrentCommand(state);
    const nodeItem = nodeItems[0];
    const nodeType = getNodeType(nodeItem);
    const sourceCatalogId = getCatalogId(sourceTree);
    const targetCatalogId = getCatalogId(targetTree);
    const betweenCatalogs = targetCatalogId !== sourceCatalogId;
    const targetCatalog = catalogConfigSelector(state, targetCatalogId);

    if (
      betweenCatalogs &&
      (nodeType !== NODE_TYPE_ITEM ||
        !targetCatalog.canPaste({
          node: nodeItem.node,
          nextParent: targetNodeItem.node,
        }))
    ) {
      return dispatch(
        newCopyPasteError(
          'Cut / paste is not allowed between these two catalogues.',
        ),
      );
    }

    let sourceTreeData = selectTreeDataByCatalogId(state, sourceCatalogId);
    const sourceNodeItems: NodeBaseItem[] = nodeItems.reduce(
      (items: NodeBaseItem[], ni: NodeBaseItem) => {
        const sourceItem = getNodeInTree({
          treeData: sourceTreeData,
          path: ni.path,
        });
        if (sourceItem) {
          items.push(sourceItem as NodeBaseItem);
        }
        return items;
      },
      [],
    );

    if (!sourceNodeItems.length) {
      return dispatch(newCopyPasteError('Source item(s) can not be found'));
    }

    const destinationName = targetNodeItem?.node?.title || '';
    sourceTreeData = removeNodesFromTree({
      paths: sourceNodeItems.map((sni) => sni.path),
      treeData: sourceTreeData,
    }).treeData;

    // Type assertion needed due to legacy JS type definitions
    let newNodes = sourceNodeItems.map((sni) =>
      createUpdatedNode({
        node: sni.node as unknown as Parameters<typeof createUpdatedNode>[0]['node'],
        nextParentNode: targetNodeItem.node,
      }),
    ) as unknown as NodeBase[];

    let nodesText =
      nodeItem.node.type === NODE_TYPE_CATEGORY ? 'Categories' : 'Items';

    if (nodeItem.node.type === NODE_TYPE_CATEGORY) {
      const result = await pasteMoveCategory(dispatch, {
        newNode: newNodes[0],
        targetNode: targetNodeItem.node,
        prevParentId: nodeItem.node.parentId,
        nextChildIndex: 0,
      });
      nodesText = result.nodesText;
    } else if (nodeItem.node.type === NODE_TYPE_ITEM) {
      const result = await pasteMoveItem(dispatch, {
        newNodes,
        targetNode: targetNodeItem.node,
        sourceNode: nodeItem.node,
        destinationName,
      });
      nodesText = result.nodesText;
      newNodes = result.filteredNodes;
    }

    let addToTreeData = betweenCatalogs
      ? selectTreeDataByCatalogId(getState(), targetCatalogId)
      : sourceTreeData;

    if (nodeItem.node.type === NODE_TYPE_ITEM && newNodes.length > 0) {
      const movedItemsCount = newNodes.length;
      const sourceCategoryId = destructId(nodeItem.node.parentId).categoryId;
      const targetCategoryId = targetNodeItem.node.category?.categoryId;

      sourceTreeData = updateMatchingNodesInTree(
        (ni: NodeBaseItem) => ni.node?.category?.categoryId === sourceCategoryId,
        sourceTreeData,
        (ni: NodeBaseItem) => ({
          category: {
            childItemCount: Math.max(
              0,
              (ni.node?.category?.childItemCount ?? 0) - movedItemsCount,
            ),
          },
        }),
      );

      addToTreeData = updateMatchingNodesInTree(
        (ni: NodeBaseItem) => ni.node?.category?.categoryId === targetCategoryId,
        betweenCatalogs ? addToTreeData : sourceTreeData,
        (ni: NodeBaseItem) => ({
          category: {
            childItemCount:
              (ni.node?.category?.childItemCount ?? 0) + movedItemsCount,
          },
        }),
      );

      if (!betweenCatalogs) {
        sourceTreeData = addToTreeData;
      }
    }

    const result = addNodesInTree(newNodes, addToTreeData, {
      expandParent: true,
      addAsFirstChild: true,
    });

    dispatch(updateData(sourceTree, sourceTreeData));

    dispatch(toastMessage(`<b>${nodesText}</b> moved to <b>${destinationName}</b>`));

    if (nodeItem.node.type === NODE_TYPE_CATEGORY) {
      const selectedNodeParentId = destructId(nodeItem.node.parentId).value;
      const selectNodeMainParentCategoryId =
        nodeItem.node.category.mainParentCategoryId;

      if (selectedNodeParentId === selectNodeMainParentCategoryId) {
        // moved category is set as main category of double exposed, update to new mainParentCategoryId
        result.treeData = updateMatchingNodesInTree(
          (ni: NodeBaseItem) =>
            ni.node?.category?.categoryId === nodeItem.node.categoryId,
          result.treeData,
          { category: { mainParentCategoryId: targetNodeItem.node.categoryId } },
        );
      }
    }

    return dispatch(pasteNode(targetTree, result.treeData));
  };
